import { lazy, Suspense } from 'react'
import { ArrowLeft, Bell, Route } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { usePermissions } from '../../auth/use-permissions'
import { EmptyState } from '../../components/empty-state'
import { Skeleton } from '../../components/ui/skeleton'
import { errorMessageKey } from '../../lib/api-error'
import { alertCentrePath } from '../alerts/routes'
import { VehicleDtcPanel } from '../diagnostics/dtc-panel'
import { useVehicleDtc } from '../diagnostics/queries'
import { historyPath } from '../trips/routes'
import { CanCapabilityCard } from './capability-notice'
import { LiveTelemetryDrawer } from './live-telemetry-drawer'
import { RawDataPanel } from './raw-data-panel'
import { VEHICLES_PATH } from './routes'
import { StateIndicators } from './state-indicators'
import {
  useCapabilities,
  useLatestTelemetry,
  useVehicle,
  useVehicleLive,
} from './use-vehicle-detail'
import { UsageTiles } from './usage-tiles'
import { VehicleHeader } from './vehicle-header'

/**
 * The tyre panel is LAZY where every other panel on this page is static.
 *
 * Importing it registers the FR/EN tyres catalogue as a side effect — see
 * `features/tyres/i18n.ts` — and this page is statically registered in
 * `routes/app-routes.tsx`, so a plain import would put ~290 lines of catalogue into the
 * entry chunk of every driver who opens a vehicle sheet to check a job. The `/tyres`
 * route already pays for that module when somebody actually goes there.
 */
const VehicleTyresPanel = lazy(async () => ({
  default: (await import('../tyres/vehicle-tyres-panel')).VehicleTyresPanel,
}))

/**
 * Vehicle detail — spec §8.1.
 *
 * The four queries are deliberately not awaited together. Identity is what the
 * page is about and gates the render; live state, capabilities and raw telemetry
 * each degrade into an explained absence, so a vehicle whose tracker has never
 * transmitted still renders its header, its class fork and the sentence that says
 * what to check. A page that showed a spinner until all four resolved would hide
 * exactly the case the empty states were written for.
 */
export function VehicleDetailPage() {
  const { t } = useTranslation()
  const { vehicleId = '' } = useParams<{ vehicleId: string }>()

  const vehicle = useVehicle(vehicleId)
  const live = useVehicleLive(vehicleId)
  const latest = useLatestTelemetry(vehicleId)
  const capabilities = useCapabilities(vehicleId)
  const dtc = useVehicleDtc(vehicleId)

  if (vehicle.isError) {
    return (
      <div className="mx-auto max-w-7xl">
        <BackLink />
        {/* A vehicle belonging to another tenant answers 404, exactly like one that
            does not exist. The UI repeats that indistinguishable answer rather than
            softening it — anything else leaks one customer's fleet shape. */}
        <EmptyState
          title={t('vehicles:detail.notFound.title')}
          description={t(errorMessageKey(vehicle.error))}
        />
      </div>
    )
  }

  if (vehicle.data === undefined) {
    return (
      <div className="mx-auto max-w-7xl space-y-4" aria-busy>
        <BackLink />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <BackLink />

      <VehicleHeader vehicle={vehicle.data} live={live.data} />

      <VehicleHistoryLinks vehicleId={vehicle.data.id} />

      <UsageTiles
        vehicle={vehicle.data}
        state={live.data?.state ?? null}
        isLoading={live.isLoading}
      />

      <LiveTelemetryDrawer
        vehicle={vehicle.data}
        live={live.data}
        capabilities={capabilities.data}
        isLoading={live.isLoading}
      />

      {/* Capability-driven, in the order a reader needs it: what the bus can say,
          then what it is saying, then the faults it has raised. The CAN card renders
          in all four states rather than only when something is wrong — a section that
          disappears once the adapter works is indistinguishable from one that failed
          to load, and the program number is worth reading on a healthy vehicle too. */}
      <CanCapabilityCard profile={capabilities.data} isLoading={capabilities.isLoading} />

      <StateIndicators state={live.data?.state ?? null} capabilities={capabilities.data} />

      <VehicleDtcPanel
        vehicleId={vehicle.data.id}
        dtc={dtc.data}
        capabilities={capabilities.data}
        isLoading={dtc.isLoading}
      />

      {/* Suivi de pneumatique. `fallback={null}` rather than a skeleton: the panel has
          its own loading state, and two spinners stacked while the chunk arrives reads
          as a broken page rather than a slow one. */}
      <Suspense fallback={null}>
        <VehicleTyresPanel vehicleId={vehicle.data.id} />
      </Suspense>

      <RawDataPanel
        latest={latest.data}
        capabilities={capabilities.data}
        isLoading={latest.isLoading}
      />
    </div>
  )
}

/**
 * The vehicle's log, one click away. Both screens already filter by vehicle through
 * the URL; what was missing was the way in from here, so "what has this car done
 * today" meant leaving the car and finding it again in a dropdown.
 */
function VehicleHistoryLinks({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const link =
    'inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground-muted transition-colors hover:bg-surface-hover hover:text-foreground'

  const showTrips = can('trips:read')
  const showAlerts = can('alerts:read')
  if (!showTrips && !showAlerts) return null

  return (
    <nav aria-label={t('vehicles:detail.historyNav')} className="flex flex-wrap gap-2">
      {showTrips ? (
        <Link to={historyPath({ vehicleId })} className={link}>
          <Route aria-hidden className="h-4 w-4" />
          {t('vehicles:detail.tripHistory')}
        </Link>
      ) : null}
      {showAlerts ? (
        <Link to={alertCentrePath({ vehicleId })} className={link}>
          <Bell aria-hidden className="h-4 w-4" />
          {t('vehicles:detail.alertHistory')}
        </Link>
      ) : null}
    </nav>
  )
}

function BackLink() {
  const { t } = useTranslation()
  return (
    <Link
      to={VEHICLES_PATH}
      className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft aria-hidden className="h-4 w-4" />
      {t('vehicles:detail.backToList')}
    </Link>
  )
}
